import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { useAllOrders } from '../hooks/useAllOrders'
import { orderSourceBadge } from '../lib/orderSource'
import type {
  AllOrdersSortField,
  AllOrdersSource,
  OrderStatus,
  OrderType,
} from '../types/order'

const STATUS_STYLES: Record<OrderStatus, string> = {
  UNPAID: 'bg-red-100 text-red-800',
  RECEIVED: 'bg-orange-100 text-orange-800',
  SCHEDULED: 'bg-cyan-100 text-cyan-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  PREPARING: 'bg-amber-100 text-amber-800',
  READY: 'bg-green-100 text-green-800',
  IN_DELIVERY: 'bg-indigo-100 text-indigo-800',
  COMPLETED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-100 text-red-800',
  REFUNDED: 'bg-purple-100 text-purple-800',
}

const STATUS_LABELS: Record<OrderStatus, string> = {
  UNPAID: 'Unpaid',
  RECEIVED: 'Needs Review',
  SCHEDULED: 'Gepland',
  CONFIRMED: 'Confirmed',
  PREPARING: 'Preparing',
  READY: 'Ready',
  IN_DELIVERY: 'In Delivery',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded',
}

const ORDER_TYPE_LABELS: Record<string, string> = {
  DINE_IN: 'Dine in',
  TAKEAWAY: 'Takeaway',
  DELIVERY: 'Delivery',
}

// UNPAID is deliberately absent — it lives in the separate research tab
const STATUS_CHIP_VALUES: OrderStatus[] = [
  'SCHEDULED',
  'RECEIVED',
  'CONFIRMED',
  'PREPARING',
  'READY',
  'IN_DELIVERY',
  'COMPLETED',
  'CANCELLED',
  'REFUNDED',
]

const SOURCE_CHIPS: { value: AllOrdersSource; label: string }[] = [
  { value: 'POS', label: 'POS' },
  { value: 'WEBSHOP', label: 'Webshop' },
  { value: 'KIOSK', label: 'Kiosk' },
  { value: 'UBER_EATS', label: 'Uber Eats' },
  { value: 'THUISBEZORGD', label: 'Thuisbezorgd' },
]

// Source badge (POS / Webshop / Kiosk / Uber Eats / Thuisbezorgd / External) lives in
// lib/orderSource.ts, shared with the delivery board so a channel reads the same everywhere.

function formatCents(cents: number) {
  return `€${(cents / 100).toFixed(2)}`
}

// History rows always show the full date + time (never time-only)
function formatDateTime(dateStr: string) {
  const ms = Number(dateStr)
  const date = isNaN(ms) ? new Date(dateStr) : new Date(ms)
  return date.toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Local-date helpers for the presets (native date input values)
const toDateInput = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const today = () => toDateInput(new Date())
const firstOfMonth = () => {
  const now = new Date()
  return toDateInput(new Date(now.getFullYear(), now.getMonth(), 1))
}
const lastMonth = (): [string, string] => {
  const now = new Date()
  return [
    toDateInput(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    toDateInput(new Date(now.getFullYear(), now.getMonth(), 0)),
  ]
}

interface FilterChipProps {
  label: string
  active: boolean
  disabled?: boolean
  onClick: () => void
}

const FilterChip = ({ label, active, disabled, onClick }: FilterChipProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
      active
        ? 'bg-blue-600 border-blue-600 text-white'
        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
  >
    {label}
  </button>
)

const SortableHeader = ({
  label,
  field,
  activeField,
  direction,
  onToggle,
}: {
  label: string
  field: AllOrdersSortField
  activeField: AllOrdersSortField
  direction: 'ASC' | 'DESC'
  onToggle: (field: AllOrdersSortField) => void
}) => (
  <th className="px-4 py-3 text-left">
    <button
      type="button"
      onClick={() => onToggle(field)}
      className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700"
    >
      {label}
      {activeField === field &&
        (direction === 'DESC' ? <ChevronDownIcon className="h-3 w-3" /> : <ChevronUpIcon className="h-3 w-3" />)}
    </button>
  </th>
)

const OrderHistoryPage: React.FC = () => {
  const navigate = useNavigate()
  const [isRefreshing, setIsRefreshing] = useState(false)

  const {
    filters,
    setFilter,
    resetFilters,
    hasActiveFilters,
    unpaidView,
    setUnpaidView,
    sortField,
    sortDirection,
    toggleSort,
    limit,
    page,
    pageCount,
    rangeLabel,
    nextPage,
    prevPage,
    offset,
    orders,
    total,
    loading,
    error,
    refetch,
  } = useAllOrders()

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await refetch()
    } finally {
      setIsRefreshing(false)
    }
  }

  const toggleInList = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter(v => v !== value) : [...list, value]

  if (error) {
    // The UNPAID-mix rejection should be unreachable (the hook never mixes),
    // but if it ever surfaces, offer a reset instead of only a raw retry.
    const isUnpaidMixError = error.message.includes('Unpaid orders can only be listed')
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Failed to load order history</h2>
          <p className="text-gray-500 mb-4">{error.message}</p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Retry
            </button>
            {isUnpaidMixError && (
              <button
                onClick={() => {
                  setUnpaidView(false)
                  resetFilters()
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Reset filters
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Order History</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading && orders.length === 0
              ? 'Loading...'
              : `${total.toLocaleString('nl-NL')} order${total !== 1 ? 's' : ''} match${total === 1 ? 'es' : ''} the current filters`}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing || loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isRefreshing || loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* View tabs: paid history vs the isolated unpaid research view */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {([
          { id: false, label: 'All orders' },
          { id: true, label: 'Unpaid (research)' },
        ] as { id: boolean; label: string }[]).map(tab => (
          <button
            key={String(tab.id)}
            onClick={() => setUnpaidView(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              unpaidView === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {unpaidView && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-fit">
          Showing unpaid orders only — these are excluded from the normal view.
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
        {/* Row 1: date range + presets + search */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
              From
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              max={filters.dateTo || undefined}
              onChange={e => setFilter('dateFrom', e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
              To
            </label>
            <input
              type="date"
              value={filters.dateTo}
              min={filters.dateFrom || undefined}
              onChange={e => setFilter('dateTo', e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setFilter('dateFrom', firstOfMonth())
                setFilter('dateTo', today())
              }}
              className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            >
              This month
            </button>
            <button
              onClick={() => {
                const [start, end] = lastMonth()
                setFilter('dateFrom', start)
                setFilter('dateTo', end)
              }}
              className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            >
              Last month
            </button>
            <button
              onClick={() => {
                setFilter('dateFrom', `${new Date().getFullYear()}-01-01`)
                setFilter('dateTo', today())
              }}
              className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            >
              This year
            </button>
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search order # or customer…"
              value={filters.search}
              onChange={e => setFilter('search', e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Row 2: status chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">Status</span>
          {STATUS_CHIP_VALUES.map(status => (
            <FilterChip
              key={status}
              label={STATUS_LABELS[status]}
              active={!unpaidView && filters.statuses.includes(status)}
              disabled={unpaidView}
              onClick={() => setFilter('statuses', toggleInList(filters.statuses, status))}
            />
          ))}
        </div>

        {/* Row 3: sources, order type, totals, discount, clear */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">Source</span>
            {SOURCE_CHIPS.map(source => (
              <FilterChip
                key={source.value}
                label={source.label}
                active={filters.sources.includes(source.value)}
                onClick={() => setFilter('sources', toggleInList(filters.sources, source.value))}
              />
            ))}
          </div>

          <select
            value={filters.orderType}
            onChange={e => setFilter('orderType', e.target.value as OrderType | '')}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
          >
            <option value="">All types</option>
            <option value="DINE_IN">Dine in</option>
            <option value="TAKEAWAY">Takeaway</option>
            <option value="DELIVERY">Delivery</option>
          </select>

          <div className="flex items-center gap-1.5">
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">€</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="Min"
                value={filters.minTotal}
                onChange={e => setFilter('minTotal', e.target.value)}
                className="w-20 pl-6 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <span className="text-gray-400 text-sm">–</span>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">€</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="Max"
                value={filters.maxTotal}
                onChange={e => setFilter('maxTotal', e.target.value)}
                className="w-20 pl-6 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {([
              { id: 'both', label: 'All' },
              { id: 'only', label: 'With discount' },
              { id: 'none', label: 'No discount' },
            ] as { id: 'both' | 'only' | 'none'; label: string }[]).map((option, index) => (
              <button
                key={option.id}
                onClick={() => setFilter('hasDiscount', option.id)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${index > 0 ? 'border-l border-gray-200' : ''} ${
                  filters.hasDiscount === option.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {hasActiveFilters && (
            <button onClick={resetFilters} className="text-sm text-gray-500 hover:text-gray-700 underline">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      {loading && orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <p className="text-sm text-gray-400">Loading orders...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-2">
          <p className="text-gray-500 font-medium">No orders found</p>
          {(hasActiveFilters || unpaidView) && (
            <p className="text-sm text-gray-400">Try adjusting your filters</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Keep previous rows visible while a page/filter change loads */}
          <div className={`overflow-x-auto transition-opacity ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Platform</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <SortableHeader label="Total" field="TOTAL_CENTS" activeField={sortField} direction={sortDirection} onToggle={toggleSort} />
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Discount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Staff</th>
                  <SortableHeader label="Created at" field="CREATED_AT" activeField={sortField} direction={sortDirection} onToggle={toggleSort} />
                  <th className="px-2 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {orders.map(order => {
                  const platform = orderSourceBadge(order)
                  return (
                    <tr
                      key={order.id}
                      onClick={() => navigate(`/orders/${order.id}`)}
                      className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-semibold text-gray-900">{order.order_number}</span>
                      </td>
                      <td className="px-4 py-3 max-w-[180px]">
                        {order.customer_name
                          ? <span className="text-sm text-gray-800 truncate block" title={order.customer_name}>{order.customer_name}</span>
                          : <span className="text-sm text-gray-400 italic">—</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${platform.classes}`}>
                          {platform.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-600">{ORDER_TYPE_LABELS[order.order_type] ?? order.order_type}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[order.status] ?? 'bg-gray-100 text-gray-700'}`}>
                            {STATUS_LABELS[order.status] ?? order.status}
                          </span>
                          {order.externally_handled && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200"
                              title="Handled externally via HubRise (not in POS revenue)"
                            >
                              Ext.
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{formatCents(order.total_cents)}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {order.discount_total_cents
                          ? <span className="text-sm text-green-700">-{formatCents(order.discount_total_cents)}</span>
                          : <span className="text-sm text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-600">{order.staffUser?.name ?? <span className="text-gray-300">—</span>}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-400">{formatDateTime(order.created_at)}</span>
                      </td>
                      <td className="px-2 py-3">
                        <ChevronRightIcon className="h-4 w-4 text-gray-300" />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Pager — driven off the exact server-side total */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <span className="text-sm text-gray-500">{rangeLabel}</span>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                Page {page} of {pageCount}
              </span>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={prevPage}
                  disabled={offset === 0 || loading}
                  className="px-3 py-1.5 text-sm font-medium bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={nextPage}
                  disabled={offset + limit >= total || loading}
                  className="px-3 py-1.5 text-sm font-medium bg-white text-gray-600 hover:bg-gray-50 border-l border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrderHistoryPage
